import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Database, RefreshCw, Loader2, ChevronDown, Upload, Download } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import api from '../../lib/api';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const IMPORT_KINDS = [
  { kind: 'skills', label: 'Skills' },
  { kind: 'roles', label: 'Roles' },
  { kind: 'members', label: 'Members' },
];

function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso); const s = (Date.now() - d.getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

// Data pill + reseed controls for the Capability Inventory / Coverage tabs,
// surfaced from the sub-nav (extreme right) so it never clutters the page body.
export default function CapabilityDataButton({ organizationId }) {
  const [status, setStatus] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [uploading, setUploading] = useState(null);
  const fileRefs = useRef({});

  const load = useCallback(() => {
    if (!organizationId) return;
    api.get(`/capability-inventory/data-status?organization_id=${organizationId}`)
      .then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, [organizationId]);
  useEffect(() => { load(); }, [load]);

  const resetDemo = async () => {
    setResetting(true);
    try {
      await api.post('/capability-inventory/reset-demo', { organization_id: organizationId });
      toast.success('Reset to demo data');
      load();
    } catch { toast.error('Reset failed (super admin only)'); }
    finally { setResetting(false); }
  };

  const downloadTemplate = async (kind) => {
    try {
      const res = await api.get(`/capability-inventory/import/${kind}/template`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a'); a.href = url; a.download = `${kind}-template.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Could not download the template.'); }
  };

  const uploadFile = async (kind, file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) { toast.error('Please upload a .csv file'); return; }
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/capability-inventory/import/${kind}?organization_id=${organizationId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const r = res.data || {};
      const errs = (r.errors || []).length;
      toast.success(`${kind}: ${r.imported ?? r.upserted ?? 'rows'} imported${errs ? `, ${errs} row error(s)` : ''}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not import ${kind}.`);
    } finally {
      setUploading(null);
      if (fileRefs.current[kind]) fileRefs.current[kind].value = '';
    }
  };

  const uploaded = status?.source === 'uploaded';
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="inline-flex items-center gap-1.5 rounded-full border border-[#2b3a5c] bg-[#111827] px-3 py-1.5 text-xs font-semibold text-slate-300 hover:text-white hover:border-[#44b6d2] transition-colors"
          data-testid="capability-data-button">
          <Database className="h-3.5 w-3.5 text-[#44b6d2]" />
          {status ? (uploaded ? 'Uploaded data' : 'Demo data') : 'Data'}
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 max-h-[80vh] overflow-y-auto bg-[#0f1729] border-[#1f2a44] text-slate-200" data-testid="capability-data-popover">
        {status?.has_data ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className={cn('inline-flex items-center gap-1.5 font-semibold px-2 py-0.5 rounded-full text-[11px]',
                uploaded ? 'bg-blue-500/15 text-blue-300' : 'bg-slate-500/20 text-slate-300')}>
                <Database className="h-3 w-3" /> {uploaded ? 'Uploaded data' : 'Demo data'}
              </span>
              <span className="text-[11px] text-slate-400">
                {uploaded
                  ? (status.last_import_at ? `imported ${relTime(status.last_import_at)}` : 'imported')
                  : (status.last_seeded_at ? `seeded ${relTime(status.last_seeded_at)}` : 'synthetic')}
              </span>
            </div>
            <p className="text-[11px] text-slate-400 tabular-nums">
              {status.counts?.members} members · {status.counts?.roles} roles · {status.counts?.skills} skills
            </p>
            {status.partial && (
              <p className="text-[11px] text-amber-300 bg-amber-500/10 rounded-lg px-2 py-1.5" data-testid="capability-data-partial">
                Partial import — {(status.partial_demo_kinds || []).map((k) => k.replace('_', ' ')).join(', ')} still demo
              </p>
            )}
            {status.can_admin && status.can_seed_demo && (
              <button onClick={resetDemo} disabled={resetting} data-testid="capability-data-reset"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-300 hover:text-white transition-colors">
                {resetting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                Reset to demo data
              </button>
            )}
            {status.can_admin && (
              <div className="border-t border-[#1f2a44] pt-3" data-testid="capability-data-import">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2">Import your own data (CSV)</p>
                <div className="space-y-1.5">
                  {IMPORT_KINDS.map(({ kind, label }) => (
                    <div key={kind} className="flex items-center justify-between gap-2">
                      <span className="text-xs text-slate-300 w-20">{label}</span>
                      <div className="flex items-center gap-2">
                        <button onClick={() => downloadTemplate(kind)} title="Download CSV template" data-testid={`capability-import-template-${kind}`}
                          className="text-slate-400 hover:text-white transition-colors"><Download className="h-3.5 w-3.5" /></button>
                        <input ref={(el) => { fileRefs.current[kind] = el; }} type="file" accept=".csv" className="hidden"
                          data-testid={`capability-import-input-${kind}`}
                          onChange={(e) => uploadFile(kind, e.target.files?.[0])} />
                        <button onClick={() => fileRefs.current[kind]?.click()} disabled={uploading === kind}
                          data-testid={`capability-import-upload-${kind}`}
                          className={cn('inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md border transition-colors',
                            uploading === kind ? 'border-slate-600 text-slate-500' : 'border-[#2b3a5c] text-slate-300 hover:border-[#44b6d2] hover:text-white')}>
                          {uploading === kind ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Upload
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-slate-500 mt-2">Uploading replaces that data kind. Download a template for the exact columns.</p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-slate-400" data-testid="capability-data-empty">No capability data loaded yet.</p>
        )}
      </PopoverContent>
    </Popover>
  );
}
